import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from './ui/badge';
import { 
  LayoutDashboard, 
  Building2, 
  Settings, 
  LogOut, 
  Clock, 
  Calendar,
  HelpCircle,
  Settings as SettingsIcon,
  Users,
  History,
  FileText,
  Package,
  UserCircle,
  Wrench,
  CheckSquare,
  Building,
  Cog,
  User,
  CreditCard,
  AlertCircle
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

function Sidebar({ user, onLogout, onCloseSidebar }) {
  const location = useLocation();
  const { t } = useLanguage();
  const [subscriptionStatus, setSubscriptionStatus] = React.useState(null);

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };
  
  // Fetch subscription status
  React.useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/status`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const status = await response.json();
          setSubscriptionStatus(status);
        }
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
      }
    };
    fetchSubscription();
  }, []);

  const menuItems = [
    {
      path: '/',
      label: t('repairWorkOrders'),
      icon: LayoutDashboard,
      description: t('repairWorkOrdersDesc')
    },
    {
      path: '/maintenance',
      label: t('maintenanceWorkOrders'),
      icon: Settings,
      description: t('maintenanceWorkOrdersDesc')
    },
    {
      path: '/calendar',
      label: t('calendar'),
      icon: Calendar,
      description: t('calendarDesc')
    },
    {
      path: '/daily-tasks',
      label: t('dailyTasks'),
      icon: Clock,
      description: t('dailyTasksDesc')
    },
    {
      path: '/maintenance-history',
      label: t('maintenanceHistory'),
      icon: History,
      description: t('maintenanceHistoryDesc')
    },
    {
      path: '/repair-history',
      label: t('repairHistory'),
      icon: FileText,
      description: t('repairHistoryDesc')
    },
    {
      path: '/departments',
      label: t('departments'),
      icon: Building2,
      description: t('departmentsDesc')
    },
    {
      path: '/machines',
      label: t('machines'),
      icon: Settings,
      description: t('machinesDesc')
    },
    {
      path: '/assignees',
      label: t('assignees'),
      icon: Users,
      description: t('assigneesDesc')
    },
    {
      path: '/parts-inventory',
      label: t('partsInventory'),
      icon: Package,
      description: t('partsInventoryDesc'),
      professionalOnly: true
    },
    {
      path: '/support',
      label: t('support'),
      icon: HelpCircle,
      description: t('supportDesc')
    }
  ];

  const filteredMenuItems = menuItems.filter(item => {
    // Check admin-only items
    if (item.adminOnly && user.role !== 'Admin') {
      return false;
    }
    
    // Check professional-only items (Parts Inventory)
    if (item.professionalOnly) {
      // Show if: user has active subscription OR is in trial
      // This allows trial users to test professional features
      return subscriptionStatus?.has_active_subscription === true;
    }
    
    return true;
  });

  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200 shadow-sm">
      {/* Logo and app name */}
      <div className="flex items-center px-6 py-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
            <SettingsIcon className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Composys MaintPro</h1>
          </div>
        </div>
      </div>

      {/* User info */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-gray-600 to-gray-700 rounded-full flex items-center justify-center">
            {user.role === 'Admin' ? (
              <SettingsIcon className="w-5 h-5 text-white" />
            ) : (
              <Users className="w-5 h-5 text-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user.full_name || user.username}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {user.role}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-4 space-y-2">
        {/* Profile Settings - Always shown at top */}
        <Link
          to="/profile"
          onClick={onCloseSidebar}
          className={`
            flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 border-b border-gray-200 mb-3
            ${isActive('/profile')
              ? 'bg-blue-50 text-blue-700 border border-blue-200' 
              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
            }
          `}
          data-testid="nav-profile"
        >
          <UserCircle className={`w-5 h-5 mr-3 ${isActive('/profile') ? 'text-blue-600' : 'text-gray-500'}`} />
          <div>
            <div>{t('profileSettings')}</div>
            <div className="text-xs text-gray-500">{t('manageAccount')}</div>
          </div>
        </Link>

        {filteredMenuItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onCloseSidebar}
              className={`
                flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                ${active 
                  ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }
              `}
              data-testid={`nav-${item.path === '/' ? 'kanban' : item.path.slice(1)}`}
            >
              <Icon className={`w-5 h-5 mr-3 ${active ? 'text-blue-600' : 'text-gray-500'}`} />
              <div>
                <div>{item.label}</div>
                <div className="text-xs text-gray-500">{item.description}</div>
              </div>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

export default Sidebar;
